import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type { PaginatedResult } from '@/types/roles';
import type {
  CreateUserBody,
  CreateUserResponse,
  ResetPasswordBody,
  ResetPasswordResponse,
  UpdateUserBody,
  UserActivityItem,
  UserDetail,
  UserListItem,
  UsersListQuery,
} from '@/types/users';

/**
 * Typed wrappers around the 8 `/api/users` endpoints from Plan 08-01.
 *
 * Every wrapper:
 *  - Throws an `ApiError` (via `toApiError`) on non-2xx so consumers can
 *    `instanceof`-switch on `e.status`.
 *  - Returns the typed response object on success.
 *
 * Permission gating is UI-only convenience (T-08-13). The backend re-checks
 * every permission via `[RequirePermission(...)]` (Plan 08-01).
 */

function buildListUrl(q: UsersListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.roleId !== undefined) params.set('roleId', String(q.roleId));
  if (q.status !== undefined) params.set('status', q.status);
  params.set('page', String(q.page ?? 1));
  params.set('pageSize', String(q.pageSize ?? 20));
  return `/api/users?${params.toString()}`;
}

export async function listUsersRequest(
  query: UsersListQuery,
): Promise<PaginatedResult<UserListItem>> {
  try {
    const { data } = await api.get<PaginatedResult<UserListItem>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getUserRequest(id: number): Promise<UserDetail> {
  try {
    const { data } = await api.get<UserDetail>(`/api/users/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Create a user. CR-01 fix: response carries `{ user, generatedPassword }`
 * — the server-generated 16-char plaintext is surfaced exactly once when the
 * caller omitted `password`. When the caller supplied an explicit value,
 * `generatedPassword` is `null` (we never re-emit a value the admin already
 * typed). Callers MUST NOT log the generated password (T-08-15).
 */
export async function createUserRequest(body: CreateUserBody): Promise<CreateUserResponse> {
  try {
    const { data } = await api.post<CreateUserResponse>('/api/users', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function updateUserRequest(id: number, body: UpdateUserBody): Promise<UserDetail> {
  try {
    const { data } = await api.put<UserDetail>(`/api/users/${String(id)}`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function deactivateUserRequest(id: number): Promise<void> {
  try {
    await api.delete(`/api/users/${String(id)}`);
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Reset a user's password. The two valid call shapes for v1.0 are:
 *
 *  - `{ password: '<explicit>', sendResetLink: false }` → backend uses the supplied password.
 *  - `{ password: null, sendResetLink: false }` → backend generates a 16-char temporary password
 *    and returns it ONCE in `ResetPasswordResponse.generatedPassword`.
 *
 * The `{ sendResetLink: true }` branch is not configured for v1.0 — backend returns
 * 501 Not Implemented (Plan 08-01 D-79). Callers SHOULD catch the resulting `ApiError`
 * with `status === 501` and fall back to the temporary-password flow.
 */
export async function resetPasswordRequest(
  id: number,
  body: ResetPasswordBody,
): Promise<ResetPasswordResponse> {
  try {
    const { data } = await api.post<ResetPasswordResponse>(
      `/api/users/${String(id)}/reset-password`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function unlockUserRequest(id: number): Promise<void> {
  try {
    await api.post(`/api/users/${String(id)}/unlock`);
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getUserActivityRequest(
  id: number,
  page = 1,
  pageSize = 20,
): Promise<PaginatedResult<UserActivityItem>> {
  try {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    const { data } = await api.get<PaginatedResult<UserActivityItem>>(
      `/api/users/${String(id)}/activity?${params.toString()}`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
